import { fetchLatestCommit, getVSCodeCreationOptions, loadVSCodeResources, showSplash } from './workbench.js';
import { BuildVersion } from './version.js';
import type * as vscode from './@types/vscode.js';
import { getDownloadCallback } from './download.js';
import serviceWorkerUrl from './sw.js?worker&url';
import { fetchManifest } from './manifest.js';

const VSCODE_STABLE_COMMIT_VERSION = '4fe60c8b1cdac1c4c174f2fb180d0d758272d713';

interface VSCodeEmbeddedInitialEventResponseData {
    theme: 'light' | 'dark';
    environmentUrl: string;
}

// Function to fetch and load VS Code resources
async function startVSCode(vscode: typeof import('./@types/vscode.js')): Promise<void> {
    try {
        const gitpodVSCodeExtensionUrl = await fetchLatestGitpodVSCodeExtension();

        let messagePorts: ReadonlyMap<string, MessagePort> | undefined;
        let configurationDefaults: Record<string, any> | undefined;
        let productConfiguration: vscode.IWorkbenchConstructionOptions['productConfiguration'] | undefined;
        let initialColorTheme: vscode.IWorkbenchConstructionOptions['initialColorTheme'] | undefined;
        if (window !== window.parent) {
            let fromUserClick = false;
            document.addEventListener('mousedown', () => {
                fromUserClick = true;
                // Reset the flag after all current event handlers and microtasks complete
                queueMicrotask(() => {
                    setTimeout(() => {
                        fromUserClick = false;
                    }, 0);
                });
            }, { capture: true });

            const origFocus = HTMLElement.prototype.focus;
            HTMLElement.prototype.focus = function (...args) {
                // Only allow focus if the iframe itself has focus or the user clicked inside it
                if (document.hasFocus() || fromUserClick) {
                    return origFocus.apply(this, args);
                }
            };

            try {
                window.parent.postMessage({
                    type: 'gitpod.requestMessagePort',
                    supportsEnvironmentUrlNavigation: true,
                }, '*');

                const initPromise = new Promise<MessageEvent<VSCodeEmbeddedInitialEventResponseData>>(resolve => {
                    const handler = (event: MessageEvent) => {
                        if (event.data?.type === 'gitpod.messagePort' && event.ports?.[0]) {
                            window.removeEventListener('message', handler);
                            resolve(event as MessageEvent<VSCodeEmbeddedInitialEventResponseData>);
                        }
                    };
                    window.addEventListener('message', handler);
                });
                const event = await initPromise;
                if (event.data.environmentUrl && new URL(event.data.environmentUrl).toString() !== new URL(document.location.href).toString()) {
                    document.location.replace(event.data.environmentUrl);
                    return;
                }

                // Listen for environment URL updates
                window.addEventListener('message', (event: MessageEvent) => {
                    if (event.data?.type === 'gitpod.updateEnvironmentUrl') {
                        if (new URL(event.data.environmentUrl).toString() !== new URL(document.location.href).toString()) {
                            document.location.replace(event.data.environmentUrl);
                        }
                    }
                });

                messagePorts = new Map([
                    ['gitpod.gitpod-flex', event.ports[0]],
                ]);
                configurationDefaults = {
                    'workbench.colorTheme': event.data.theme === 'dark' ? 'Gitpod Dark' : 'Gitpod Light',
                    'workbench.preferredLightColorTheme': 'Gitpod Light',
                    'workbench.preferredDarkColorTheme': 'Gitpod Dark',
                    'editor.minimap.enabled': false,
                    'window.commandCenter': false,
                    'workbench.layoutControl.enabled': false,
                    'editor.fontSize': 12,
                    'workbench.startupEditor': 'none',
                    'workbench.secondarySideBar.defaultVisibility': 'hidden',
                };
                initialColorTheme = {
                    themeType: (event.data.theme === 'dark' ? 'dark' : 'light') as any,
                    // should be aligned with https://github.com/gitpod-io/gitpod-vscode-theme
                    colors: event.data.theme === 'dark'
                        ? {
                                'activityBar.background': '#292524',
                                'activityBar.foreground': '#f3f3f3',
                                'activityBarBadge.background': '#007acc',
                                'button.background': '#d67400',
                                'button.foreground': '#ffffff',
                                'editor.background': '#12100c',
                                'editor.foreground': '#d4d4d4',
                                'editor.inactiveSelectionBackground': '#7a4200',
                                'editor.selectionBackground': '#a35800',
                                'editor.selectionHighlightBackground': '#add6ff26',
                                'editorIndentGuide.activeBackground': '#707070',
                                'editorIndentGuide.background': '#404040',
                                'input.placeholderForeground': '#a6a6a6',
                                'list.activeSelectionBackground': '#57534e',
                                'list.activeSelectionForeground': '#f3f3f3',
                                'list.activeSelectionIconForeground': '#ffffff',
                                'list.dropBackground': '#383b3d',
                                'list.inactiveSelectionBackground': '#44403c',
                                'list.inactiveSelectionForeground': '#f3f3f3',
                                'menu.background': '#252526',
                                'menu.foreground': '#cccccc',
                                'minimap.background': '#12100c',
                                'minimapSlider.activeBackground': '#44403c',
                                'ports.iconRunningProcessForeground': '#369432',
                                'sideBar.background': '#1c1917',
                                'sideBarSectionHeader.background': '#252526',
                                'sideBarSectionHeader.border': '#cccccc33',
                                'sideBarTitle.foreground': '#bbbbbb',
                                'statusBar.background': '#252526',
                                'statusBar.debuggingBackground': '#d67400',
                                'statusBar.foreground': '#f3f3f3',
                                'statusBar.noFolderBackground': '#d67400',
                                'statusBarItem.remoteBackground': '#d67400',
                                'statusBarItem.remoteForeground': '#f9f9f9',
                                'tab.inactiveBackground': '#1c1917',
                                'tab.lastPinnedBorder': '#cccccc33',
                                'textLink.activeForeground': '#ff8a00',
                                'textLink.foreground': '#ffb45b',
                            }
                        : {
                                'activityBar.background': '#f9f9f9',
                                'activityBar.foreground': '#292524',
                                'activityBarBadge.background': '#007acc',
                                'button.background': '#ff8a00',
                                'button.foreground': '#ffffff',
                                'editor.background': '#ffffff',
                                'editor.foreground': '#000000',
                                'editor.inactiveSelectionBackground': '#ffe4bc',
                                'editor.selectionBackground': '#ffe4bc',
                                'editor.selectionHighlightBackground': '#add6ff80',
                                'editorIndentGuide.activeBackground': '#939393',
                                'editorIndentGuide.background': '#d3d3d3',
                                'editorSuggestWidget.background': '#f3f3f3',
                                'input.placeholderForeground': '#767676',
                                'list.activeSelectionBackground': '#e7e5e4',
                                'list.activeSelectionForeground': '#292524',
                                'list.activeSelectionIconForeground': '#ffffff',
                                'list.hoverBackground': '#e8e8e8',
                                'list.inactiveSelectionBackground': '#f9f9f9',
                                'list.inactiveSelectionForeground': '#292524',
                                'minimap.background': '#fcfcfc',
                                'minimapSlider.activeBackground': '#f9f9f9',
                                'notebook.cellBorderColor': '#e8e8e8',
                                'notebook.selectedCellBackground': '#c8ddf150',
                                'ports.iconRunningProcessForeground': '#369432',
                                'searchEditor.textInputBorder': '#cecece',
                                'settings.numberInputBorder': '#cecece',
                                'settings.textInputBorder': '#cecece',
                                'sideBar.background': '#fcfcfc',
                                'sideBarSectionHeader.background': '#f9f9f9',
                                'sideBarSectionHeader.border': '#61616130',
                                'sideBarTitle.foreground': '#6f6f6f',
                                'statusBar.background': '#f3f3f3',
                                'statusBar.debuggingBackground': '#ff8a00',
                                'statusBar.foreground': '#292524',
                                'statusBar.noFolderBackground': '#ff8a00',
                                'statusBarItem.errorBackground': '#c72e0f',
                                'statusBarItem.remoteBackground': '#ff8a00',
                                'statusBarItem.remoteForeground': '#f9f9f9',
                                'tab.inactiveBackground': '#f9f9f9',
                                'tab.lastPinnedBorder': '#61616130',
                                'textLink.activeForeground': '#ff8a00',
                                'textLink.foreground': '#ffb45b',
                            },
                };
                productConfiguration = {};
            } catch (err) {
                console.warn('Failed to set up message port for gitpod-flex extension:', err);
            }
        }

        let remoteResourceProvider: vscode.IRemoteResourceProvider | undefined;
        if ('serviceWorker' in navigator) {
            const { quality, commit, baseUrl, trustedTypesPolicy } = (globalThis as any)._VSCODE_WEB_BOOTSTRAP_;

            // Use relative path to enable Vite asset hashing
            const swPromise = navigator.serviceWorker.register(
                trustedTypesPolicy?.createScriptURL(serviceWorkerUrl) ?? serviceWorkerUrl,
                { type: 'module' },
            );
            swPromise.then(async swRegistration => {
                let sw = swRegistration.active || swRegistration.waiting;
                if (!sw) {
                    sw = await new Promise<ServiceWorker>(resolve => {
                        if (swRegistration.active) {
                            return resolve(swRegistration.active);
                        }
                        if (!swRegistration.installing) {
                            throw new Error('Unnexpected service worker state');
                        }
                        const swIntalling = swRegistration.installing;
                        const onStateChange = (e: any) => {
                            if ('activated' === (e.target.state as ServiceWorkerState)) {
                                swIntalling.removeEventListener('statechange', onStateChange);
                                return resolve(e.target as ServiceWorker);
                            }
                        };
                        swIntalling.addEventListener('statechange', onStateChange);
                    });
                }
                sw.addEventListener('activate', event => {
                    console.log('Service worker activating');
                });
            }).catch(error => {
                console.error('Service worker registration failed', error);
            });

            let loopbackEventCallback: ((e: vscode.IRemoteResourceRequest) => unknown) | undefined;
            navigator.serviceWorker.addEventListener('message', event => {
                if (event.origin === window.location.origin && 'loopback' === event.data.type && event.source) {
                    console.log(`MAIN: received loopback request ${event.data.id}`);
                    loopbackEventCallback?.({
                        uri: vscode.URI.parse(event.data.uri),
                        respondWith(statusCode: number, body: Uint8Array, headers: Record<string, string>) {
                            const dataBuffer = body.byteLength === body.buffer.byteLength
                                ? body.buffer
                                : body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength);
                            event.source!.postMessage({
                                type: 'loopback',
                                id: event.data.id,
                                status: statusCode,
                                body: dataBuffer,
                                headers,
                            }, {
                                targetOrigin: event.origin,
                                transfer: [dataBuffer],
                            });
                        },
                    });
                }
            });

            remoteResourceProvider = {
                path: '/loopback',
                onDidReceiveRequest: e => {
                    loopbackEventCallback = e;
                    return {
                        dispose: () => {
                            loopbackEventCallback = undefined;
                        },
                    };
                },
            };
        } else {
            console.warn('No service worker available');
        }

        let remoteAuthority: string | undefined;
        let folderUri: vscode.URI | undefined;
        let extensionsSyncStateVersion: string | undefined;

        const url = new URL(document.location.href);
        let workspaceFolder: string | undefined;
        if (url.pathname.startsWith('/environment/')) {
            let destination = url.pathname.slice('/environment/'.length);

            const nextSlash = destination.indexOf('/', 1);
            if (nextSlash !== -1) {
                workspaceFolder = destination.slice(nextSlash);
                destination = destination.slice(0, nextSlash);
            }

            remoteAuthority = `environment+${destination}`;
            folderUri = vscode.URI.parse(`vscode-remote://${remoteAuthority}${workspaceFolder || '/workspace'}`);

            extensionsSyncStateVersion = destination;
        }

        const doDownload = getDownloadCallback(vscode);

        const commands: vscode.ICommand[] = [];
        commands.push({
            id: 'vscode.browser:openPath',
            handler: (path: string, options?: { newTab: boolean }) => {
                if (path.startsWith('/environment/')) {
                    if (options?.newTab) {
                        window.open(path, '_blank');
                    } else {
                        window.location.pathname = path;
                    }
                }
            },
        });
        commands.push({
            id: 'vscode.browser:download',
            handler: async (
            // vscode wraps Uint8Array into a VSBuffer and this is its shape.
                dataOrUri: { buffer: Uint8Array; byteLength: number } | vscode.URI,
                name: string,
            ) => {
                doDownload(vscode.URI.isUri(dataOrUri) ? dataOrUri : dataOrUri.buffer, name);
            },
        });
        commands.push({
            id: 'gitpod-flex.getAdditionalConnectionInfo',
            handler: () => {
                return {
                    workspaceFolder,
                    userId: url.searchParams.get('userId') || undefined,
                    endpoint: url.searchParams.get('endpoint') || undefined,
                };
            },
        });
        const creationOptions = await getVSCodeCreationOptions(vscode, folderUri);

        if (folderUri) {
            commands.push({
                id: 'vscode.browser:close',
                label: 'Close Remote Workspace',
                menu: [vscode.Menu.CommandPalette, vscode.Menu.StatusBarWindowIndicatorMenu],
                handler: () => creationOptions.workspaceProvider?.open(undefined, { reuse: true }),
            });
        }

        vscode.create(document.body, {
            ...creationOptions,
            additionalTrustedDomains: [
                'https://*.gitpod.io',
            ],
            enableWorkspaceTrust: false,
            settingsSyncOptions: {
                enabled: true,
                extensionsSyncStateVersion,
            },
            productConfiguration: {
                ...productConfiguration,
                embedderIdentifier: 'vscode.gitpod.io',
                extensionEnabledApiProposals: {
                    'gitpod.gitpod-flex': ['ipc', 'resolvers', 'tunnels', 'contribViewsWelcome'],
                },
                trustedExtensionAuthAccess: ['gitpod.gitpod-flex', 'vscode.git', 'vscode.github', 'ms-vscode.remote-repositories', 'github.remotehub', 'ms-vscode.azure-repos', 'ms-vscode.remote-server', 'github.vscode-pull-request-github', 'github.codespaces', 'ms-vsliveshare.vsliveshare', 'github.copilot', 'github.copilot-chat'],
                webEndpointUrlTemplate: 'https://{{uuid}}.vscode-gitpod-cdn.com/vscode-cdn.net/{{quality}}/{{commit}}',
                webviewContentExternalBaseUrlTemplate: 'https://{{uuid}}.vscode-gitpod-cdn.com/vscode-cdn.net/{{quality}}/{{commit}}/out/vs/workbench/contrib/webview/browser/pre/',
                chatParticipantRegistry: 'https://main.vscode-gitpod-cdn.com/vscode-cdn.net/extensions/chat.json',
                extensionsGallery: {
                    nlsBaseUrl: 'https://www.vscode-gitpod-cdn.com/vscode-unpkg.net/_lp/',
                    controlUrl: 'https://main.vscode-gitpod-cdn.com/vscode-cdn.net/extensions/marketplace.json',
                    resourceUrlTemplate: 'https://{publisher}.vscode-gitpod-cdn.com/vscode-unpkg.net/{publisher}/{name}/{version}/{path}',
                    extensionUrlTemplate: 'https://www.vscode-gitpod-cdn.com/vscode-unpkg.net/_gallery/{publisher}/{name}/latest',
                } as any,
                tasConfig: undefined,
                aiConfig: undefined,
                msftInternalDomains: undefined,
                npsSurveyUrl: undefined,
                surveys: undefined,
            },
            additionalBuiltinExtensions: [
                vscode.URI.parse(gitpodVSCodeExtensionUrl),
            ],
            commands,
            remoteResourceProvider,
            remoteAuthority,
            messagePorts,
            configurationDefaults,
            initialColorTheme,
        });

    } catch (e) {
        console.error('Failed to start vscode workbench', e);
        throw e;
    }
}

function isLatest() {
    try {
        const url = new URL(document.location.href);
        const vscodeQuality = url.searchParams.get('vscode-quality');
        return vscodeQuality === 'latest';
    } catch (e) {
        console.error('Failed to parse URL for insider check', e);
        return false;
    }
}

// Function to fetch the latest gitpod web extension version
async function fetchLatestGitpodVSCodeExtension(): Promise<string> {
    // return 'http://127.0.0.1:5000'; // dev mode

    const quality = isLatest() ? 'latest' : 'stable';

    // Fetch manifest directly - this will set the preferred base URL
    // which the service worker will use for subsequent resource requests
    const result = await fetchManifest(quality);
    return result.extensionUrl;
}

(async () => {
    showSplash();

    try {
        const commit = isLatest() ? await fetchLatestCommit() : VSCODE_STABLE_COMMIT_VERSION;

        const vscode = await loadVSCodeResources(commit);
        await startVSCode(vscode);
    } catch (e) {
        throw e;
    }
})();

console.log('version', BuildVersion);
